// src/App.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MapComponent from './MapComponent';
import { calculateRegression } from './components/RegressionCalculator';
import { getChartData, chartOptions } from './components/ChartConfig';
import { ScatterChartData, ScatterChartOptions } from './components/ScatterChartConfig';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import 'chart.js/auto';
import 'bootstrap/dist/css/bootstrap.min.css';



function mannKendallTest(x) {
  const n = x.length;
  let S = 0;
  // count concordant vs. discordant
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      S += Math.sign(x[j] - x[i]);
    }
  }
  // var(S)
  const varS = (n * (n - 1) * (2 * n + 5)) / 18;
  // compute z
  let z;
  if (S > 0)      z = (S - 1) / Math.sqrt(varS);
  else if (S < 0) z = (S + 1) / Math.sqrt(varS);
  else            z = 0;
  // two-sided p
  const pValue = 2 * (1 - 0.5 * (1 + erf(Math.abs(z) / Math.SQRT2)));
  // τ = S / (0.5 * n (n-1))
  const tau = S / (0.5 * n * (n - 1));
  return { tau, pValue };
}

// approximate error function
function erf(x) {
  // Abramowitz & Stegun 7.1.26
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const a1 =  0.254829592;
  const a2 = -0.284496736;
  const a3 =  1.421413741;
  const a4 = -1.453152027;
  const a5 =  1.061405429;
  const p  =  0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5*t + a4)*t) + a3)*t + a2)*t + a1)*t*Math.exp(-x*x);
  return sign * y;
}



// ─────────────────────────────────────────────────────────────────────────────
// Helper functions must be declared BEFORE the App component so they're in scope
// ─────────────────────────────────────────────────────────────────────────────
// Replace your old rankArray() with this:
function rankArrayWithTies(arr) {
  // Pair each value with its original index
  const pairs = arr.map((v, i) => ({ v, i }));
  // Sort ascending by value
  pairs.sort((a, b) => a.v - b.v);

  const ranks = new Array(arr.length);
  let start = 0;

  while (start < pairs.length) {
    // find block of equal values
    let end = start + 1;
    while (end < pairs.length && pairs[end].v === pairs[start].v) {
      end++;
    }
    // average rank for ties: ranks are 1-based
    const avgRank = (start + 1 + end) / 2;
    for (let k = start; k < end; k++) {
      ranks[pairs[k].i] = avgRank;
    }
    start = end;
  }

  return ranks;
}

// Then update calculateSpearmanCorrelation to use it:
function calculateSpearmanCorrelation(data) {
  
const pts = data.filter(d => d.value != null && d.value2 != null);

  const n     = pts.length;
  const xVals = pts.map(d => d.value);
  const yVals = pts.map(d => d.value2);


  // use tie-aware ranks
  const xRanks = rankArrayWithTies(xVals);
  const yRanks = rankArrayWithTies(yVals);

  const meanX = xRanks.reduce((sum, v) => sum + v, 0) / n;
  const meanY = yRanks.reduce((sum, v) => sum + v, 0) / n;

  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xRanks[i] - meanX;
    const dy = yRanks[i] - meanY;
    num  += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }

  return num / Math.sqrt(denX * denY);
}

/**
 * Turn an array of { /* … other props …, value } into
 * [{ year, total }, …], by auto‑detecting the date field.
 */
function aggregateByYear(rawData) {
  const sums = rawData.reduce((acc, item) => {
    // 1) pick the first property that “looks like” a date string
    const dateKey = 
      // your preferred names
      ['date','fdt_date_time','datetime','timestamp']
        .find(k => typeof item[k] === 'string' && !isNaN(Date.parse(item[k]))) ||
      // fallback: anything that matches YYYY‑MM‑DD
      Object.keys(item).find(k => /^\d{4}-\d{2}-\d{2}/.test(item[k])) ||
      null;

    if (!dateKey) return acc;
    
    const year = new Date(item[dateKey]).getFullYear();
    if (isNaN(year)) return acc;   // skip bad dates

    acc[year] = (acc[year]||0) + (item.value||0);
    return acc;
  }, {});

  return Object
    .entries(sums)
    .map(([year, total]) => ({ year: +year, total }))
    .sort((a,b) => a.year - b.year);
}



// ─────────────────────────────────────────────────────────────────────────────
// Main App component
// ─────────────────────────────────────────────────────────────────────────────
function App() {
const [mkTau, setMkTau]         = useState(null);
const [mkPValue, setMkPValue]   = useState(null);


  // — state hooks —
  const [message, setMessage]                   = useState('');
  const [error, setError]                       = useState('');
  const [data, setData]                         = useState([]);
  const [annualData, setAnnualData]             = useState([]);
  const [regressionLine, setRegressionLine]     = useState([]);
  const [isCompareMode, setIsCompareMode]       = useState(false);
  const [freezeCheckbox, setFreezeCheckbox]     = useState(false); // control disabling
  const [variable1, setVariable1]               = useState('');
  const [variable2, setVariable2]               = useState('');
  const [variable3, setVariable3]               = useState('');   // subbasin param
  const [clickedPoint, setClickedPoint]         = useState(null);
  const [isSubmit, setIsSubmit]                 = useState(false);
  const [slope, setSlope]                       = useState(0);
  const [spearmanCorrelation, setSpearmanCorrelation] = useState(0);
  const [pointId, setPointId]                   = useState({ id: null, name: '' });
  const [pointLayer, setPointLayer] = useState('sites'); 
  const [isClick, setIsClick]                   = useState(false);
  const [viewMode, setViewMode]                 = useState('time'); // 'time' | 'annual'
  const [isBasin, setIsbasin]                   = useState(false);

  // — unit mappings for chart axes —
  const unitMapping = {
    ph: '',
    temperature: '°C',
    do_mg_l: 'mg/L',
    nitrogen_mg_l: 'mg/L',
    fdt_specific_conductance: 'µS/cm',
    ammonia_mg_l: 'mg/L',
    nox_mg_l: 'mg/L',
    nitrogen_kjeldahl_total_00625_mg_l: 'mg/L',
    phosphorus_total_00665_mg_l: 'mg/L',
    phosphorus_total_orthophosphate_70507_mg_l: 'mg/L',
    hardness_total_00900_mg_l: 'mg/L',
    chloride_mg_l: 'mg/L',
    sulfate_mg_l: 'mg/L',
    ecoli: 'CFU/100mL',
    enterococci: 'CFU/100mL',
    fecal_coli: 'CFU/100mL',
    chlorophyll_a_ug_l: 'µg/L',
    tss_mg_l: 'mg/L',
    secchi_depth_m: 'm',
    nox_tkn_sum: 'mg/L',
    tn:        'kg/ha',     // Total N
    tp:        'kg/ha',     // Total P
    sediments: 'tons/ha'    // Sediment Yield
  };

  // — list of site‐level variables —
  const variables = [
    { value: 'ph', label: 'pH' },
    { value: 'temperature', label: 'Temperature' },
    { value: 'do_mg_l', label: 'DO' },
    { value: 'nitrogen_mg_l', label: 'Nitrogen' },
    { value: 'ammonia_mg_l', label: 'Ammonia' },
    { value: 'nox_mg_l', label: 'NOx' },
    { value: 'nitrogen_kjeldahl_total_00625_mg_l', label: 'Total Kjeldahl Nitrogen' },
    { value: 'phosphorus_total_00665_mg_l', label: 'Total Phosphorus' },
    { value: 'phosphorus_total_orthophosphate_70507_mg_l', label: 'Orthophosphate' },
    { value: 'hardness_total_00900_mg_l', label: 'Total Hardness' },
    { value: 'chloride_mg_l', label: 'Chloride' },
    { value: 'sulfate_mg_l', label: 'Sulfate' },
    { value: 'ecoli', label: 'E. coli' },
    { value: 'fecal_coli', label: 'Fecal Coliform' },
    { value: 'chlorophyll_a_ug_l', label: 'Chlorophyll a' },
    { value: 'tss_mg_l', label: 'Total Suspended Solids' },
    { value: 'secchi_depth_m', label: 'Secchi Depth' },
    { value: 'nox_tkn_sum', label: 'NOx + TKN' }
  ];

  // — chart.js config for time‐series —
  const selectedUnit = unitMapping[variable1];
  const varObj1      = variables.find(v => v.value === variable1);
  const label1       = varObj1 ? varObj1.label : 'Value';
  const myChartOpts  = chartOptions(selectedUnit, label1);

  // — scatter‐compare config —
  const unitX  = unitMapping[variable1];
  const unitY  = unitMapping[variable2];
  const labelX = variables.find(v => v.value === variable1)?.label || 'X';
  const labelY = variables.find(v => v.value === variable2)?.label || 'Y';


const isSubbasin = ['Blackwater Subbasin','Roanoke River Subbasin']
.includes(pointId.name);



  // — always show vertical scrollbar —
  useEffect(() => {
    document.body.style.overflowY = 'scroll';
    return () => { document.body.style.overflowY = 'auto'; };
  }, []);

  // — map subbasin dropdown to actual field key —
  useEffect(() => {
    if (
      !isCompareMode &&
      ['Blackwater Subbasin', 'Roanoke River Subbasin'].includes(pointId.name)
    ) {
      switch (variable3) {
        case 'total_n':
          setVariable1('tn');
          break;
        case 'total_p':
          setVariable1('tp');
          break;
        case 'sediments':
          setVariable1('sediments');
          break;
        default:
          break;
      }
    }
  }, [variable3, pointId, isCompareMode]);

  useEffect(()=>{
    setIsCompareMode(false)
  },[variable3])
  // — auto-submit when all selections exist —
  useEffect(() => {
    if (!pointId.id) return;
    if (isCompareMode ? (variable1 && variable2) : variable1) {
      const needsSub = ['Blackwater Subbasin','Roanoke River Subbasin']
        .includes(pointId.name);
      if (!needsSub || (needsSub && variable3)) {
        handleSubmit();
      }
    }
    if (variable1.includes('tn') || 
        variable1.includes('tp')||
        variable1.includes('sediments')){
        setIsCompareMode(false);
        setFreezeCheckbox(true);
    }
    else{
      setFreezeCheckbox(false);
      //setVariable3('')
    }
  }, [variable1, variable2, variable3, isCompareMode, pointId]);

  // — handle map click —
 const handleMapClick = (lat, lng, id, name, layer) => {
    setClickedPoint({ lat, lng });
    setPointId({ id, name });
    setPointLayer(layer); 
    setIsClick(true);
  };

  // — submit & fetch data —
  const handleSubmit = async () => {
    setIsClick(false);
    setError('');
    setData([]);
    setRegressionLine([]);
    setIsSubmit(false);

    const payload = {
      mode: isCompareMode ? 'compare' : 'single',
      variable1,
      variable2: isCompareMode ? variable2 : null,
      subParam: variable3 || null,
      point: clickedPoint,
      id: pointId.id,
      layer:     pointLayer,      
    };

    try {
       const { data: resp } = await axios.post('/api/submit/', payload);
      setMessage(resp.message);
      // if (variable1.includes('tn') 
      //   || variable1.includes('tp')||variable1.includes('sediments')){
      //     setIsCompareMode(false)
      //     setFreezeCheckbox(true)    
      // }
      // else{
      //   setFreezeCheckbox(false)  
      // }
      const rd = resp.return_data;
      if (!Array.isArray(rd) || rd.length === 0) {
        setError('No Data for the selected variable');
        setIsSubmit(true);
        return;
      }

      setData(rd);
      setAnnualData(aggregateByYear(rd));

      if (isCompareMode) {
        setSpearmanCorrelation(calculateSpearmanCorrelation(rd));
      } else {
        const { regressionLine, senSlope } = calculateRegression(rd);
        setRegressionLine(regressionLine);
        setSlope(senSlope);

      // 1. build the annual totals array
      const annualTotals = aggregateByYear(rd).map(d => d.total);
      // 2. run the test
      const { tau, pValue } = mannKendallTest(annualTotals);
      // 3. update state
      setMkTau(tau);
      setMkPValue(pValue);



      }

      setError('');
      setIsSubmit(true);
    } catch {
      setError('No Data for the selected variable');
      setIsSubmit(true);
    }
  };

  // — filter out null values —
  const validData = isCompareMode
    ? data.filter(d => d.value != null && d.value2 != null)
    : data.filter(d => d.value != null);

  // — prepare line‐chart data —
  const chartData = (!isCompareMode && isSubmit && !isClick && regressionLine.length)
    ? getChartData(validData, regressionLine)
    : getChartData([], []);

  // — prepare scatter‐chart data —
  const scatterData = (isCompareMode && isSubmit && !isClick)
    ? ScatterChartData(validData)
    : ScatterChartData([]);

  // — prepare bar‐chart data & options —
  const barData = {
    labels: annualData.map(d => d.year.toString()),
    datasets: [{
      label: label1 + ' (Annual)',
      data:  annualData.map(d => d.total)
    }]
  };
  const barOpts = {
    ...myChartOpts,
    scales: {
      x: { title: { display: true, text: 'Year' } },
      y: { title: { display: true, text: label1 + ` (${selectedUnit})` } }
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1 className="text-center">SML Algal Bloom Study</h1>
      <div className="container-fluid">
        <div className="row">

          {/* Map */}
          <div className="col-md-6" style={{ height: '100vh' }}>
            <MapComponent onMapClick={handleMapClick} />
          </div>

          {/* Controls & Charts */}
          <div className="col-md-6">
            <div className="container mt-4">
              <div className="row">

                {/* Header */}
                <div className="col-12 mb-3">
                  {pointId.id && (
                    <p className="text-primary fw-bold">{pointId.name}</p>
                  )}
                </div>

                {/* Variable selectors + Compare toggle */}
                {!isSubbasin && (
                <div className="col-12 mb-3">
                  <label className="form-label me-2">Select Variable:</label>
                  {!isCompareMode ? (
                    <select
                      className="form-select"
                      style={{ width: '200px', display: 'inline-block' }}
                      value={variable1}
                      onChange={e => setVariable1(e.target.value)}
                    >
                      <option value=""></option>
                      {variables.map(v => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <select
                        className="form-select me-2"
                        style={{ width: '200px', display: 'inline-block' }}
                        value={variable1}
                        onChange={e => setVariable1(e.target.value)}
                      >
                        <option value=""></option>
                        {variables.map(v => (
                          <option key={v.value} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select"
                        style={{ width: '200px', display: 'inline-block' }}
                        value={variable2}
                        onChange={e => setVariable2(e.target.value)}
                      >
                        <option value=""></option>
                        {variables.map(v => (
                          <option key={v.value} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                    </>
                  )}

                  <div className="form-check form-switch d-inline-block ms-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isCompareMode}
                      onChange={() => setIsCompareMode(!isCompareMode)}
                      disabled = {freezeCheckbox}
                    />
                    <label className="form-check-label">Compare</label>
                  </div>

                  <button
                    className="btn btn-primary ms-3"
                    onClick={handleSubmit}
                    disabled={
                      !pointId.id ||
                      (!variable1 && !isCompareMode) ||
                      (['Blackwater Subbasin','Roanoke River Subbasin']
                        .includes(pointId.name) && !variable3)
                    }
                  >
                    Submit
                  </button>
                </div>
                )}

                {/* Chart area + Subbasin dropdown */}
                <div className="col-12 border-top pt-3">

{/* inside your render, replace the entire {!isCompareMode && isSubmit && ( … )} block with: */}
{!isCompareMode && isSubmit && (
  <>
    {/* always show Sen’s slope */}
  {/*  <p className="fw-bold">Sen’s Slope: {slope.toFixed(4)}</p> */}

<p className="fw-bold">
  Sen’s Slope: {slope.toFixed(4)} {selectedUnit}/year
</p>

  {/* simplified MK result */}
    <p className="fw-bold">
      Mann–Kendall Trend:{' '}
      {mkPValue !== null
        ? (mkPValue < 0.05 ? 'Significant' : 'Non-significant')
        : '–'}
    </p>



    {/* heading: for sites it’s always “Linear Regression” */}
    <h4 className="text-center"></h4>
    
    {/* only for the two basin polygons, show the toggle & allow Annual Bar */}
    {isSubbasin && (
      <>
        <div className="btn-group mb-3">
          <button
            className={`btn btn-${viewMode==='time'?'primary':'outline-primary'}`}
            onClick={() => setViewMode('time')}
          >
            Time Series
          </button>
          <button
            className={`btn btn-${viewMode==='annual'?'primary':'outline-primary'}`}
            onClick={() => setViewMode('annual')}
          >
            Annual Bar
          </button>
        </div>
        {/* switch heading when they pick Annual */}
        <h4 className="text-center">
          {viewMode==='time' ? '' : 'Annual Aggregate'}
        </h4>
      </>
    )}

    {error && <p className="text-danger">{error}</p>}

    <div style={{ height: 400, width: '100%' }}>
      {/* if it’s a site (¬isSubbasin) OR they selected Time, always show the Line */}
      {(!isSubbasin || viewMode==='time') && (
        <Line data={chartData} options={myChartOpts} />
      )}
      {/* if it’s a basin AND they selected Annual, show the Bar */}
      {isSubbasin && viewMode==='annual' && (
        <Bar  data={barData}  options={barOpts}  />
      )}
    </div>
  </>
)}


                  {isCompareMode && isSubmit && (
                    <>
                      <p className="fw-bold">
                        Spearman’s Corr.: {spearmanCorrelation.toFixed(4)}
                      </p>






                      <h4 className="text-center"></h4>
                      {error && <p className="text-danger">{error}</p>}
                      <div style={{ height: 400, width: '100%' }}>
                        <Scatter
                          data={scatterData}
                          options={ScatterChartOptions(unitX, unitY, labelX, labelY)}
                        />
                      </div>
                    </>
                  )}

                  {/* Subbasin param selector */}
                  {['Blackwater Subbasin','Roanoke River Subbasin']
                    .includes(pointId.name) && (
                    <div className="mt-3">
                      <label className="form-label me-2">
                        Select Subbasin Parameter:
                      </label>
                      <select
                        className="form-select"
                        style={{ width: '200px', display: 'inline-block' }}
                        value={variable3}
                        onChange={e => setVariable3(e.target.value)}
                      >
                        <option value=""></option>
                        <option value="total_n">Total N</option>
                        <option value="total_p">Total P</option>
                        <option value="sediments">Sediments</option>
                      </select>
                    </div>
                  ) }

                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default App;
